import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { TextField } from '@material-ui/core';
import { ConfigHeader } from '@sierralabs/forms-ui';
import ConfigCard from '@sierralabs/forms-ui/dist/config/components/ConfigCard';
import ConfigContent from '@sierralabs/forms-ui/dist/config/components/ConfigContent';
import ConfigFieldControl from '@sierralabs/forms-ui/dist/config/components/ConfigFieldControl';
import {
  Connector,
  Diagram,
  DiagramType,
  Shape,
  ShapeType,
  StateNode,
  StateNodeType,
  Workflow,
  WorkflowDiagram
} from '@sierralabs/workflow';

import { FetchError } from '../../common/api/api';
import AppSnackbar from '../../common/AppSnackbar';
import ColorSelector, { StatusColor } from '../../common/ColorSelector';
import SplitPane, { Pane } from '../../common/SplitPane';
import { useTimeout } from '../../common/timeout.hook';
import { Group } from '../groups/Group.interface';
import { TaskTypeEntity } from '../task-types/TaskType.interface';
import EditableFieldSection from './components/EditableFieldSection';
import StateEventEditorContainer from './components/StateEventEditorContainer';
import StateInvokeActionEditor from './components/StateInvokeActionEditor';
import StateNodeTypeSelector from './components/StateNodeTypeSelector';
import GroupPermissionsSection from './components/GroupPermissionsSection';
import {
  useFindWorkflow,
  useFindWorkflows,
  useUpdateWorkflowConfig
} from './Workflow.hooks';
import { BaseWorkflowTabProps, WorkflowDto } from './Workflow.interface';

const Styled = {
  SplitPane: styled(SplitPane)`
    .resizer {
      z-index: 1;
    }
  `,
  EditorPane: styled(Pane)`
    display: flex;
    flex: 1;
    flex-direction: column;
    overflow: hidden;
  `,
  Configurator: styled.div`
    width: 100%;
    height: 100%;
    overflow-y: auto;
    padding-bottom: 64px;
    background: #fff;
    /* .config-header {
      display: flex;
      padding: 7px 10px;
      align-items: center;
      border-top: solid 1px #ccc;
      .header-actions {
        display: flex;
        flex: 1;
        justify-content: flex-end;
      }
    } */
    .config-content {
      padding: 10px;
      margin-bottom: 10px;
    }
    .form-fields {
      display: flex;
      flex-direction: column;
      width: 100%;
      .field {
        margin-top: 10px;
        margin-bottom: 10px;
      }
    }
  `
};

export interface WorkflowEditPageProps extends BaseWorkflowTabProps {
  config?: WorkflowDto['config'];
  onChangeConfig?: () => void;
}

const WorkflowEditPage: React.FC<WorkflowEditPageProps> = (
  props: WorkflowEditPageProps
) => {
  const { config, onChange, onChangeConfig } = props;
  const { workflowId: paramWorkflowId = '' } = useParams<{
    workflowId: string;
  }>();
  const workflowId: number = parseInt(paramWorkflowId) || 0;
  const shouldRefreshRef = useRef<boolean>(false);
  const [{ workflow }, setWorkflowState] = useState<{
    workflow?: Workflow;
  }>({ workflow: undefined });
  const [, setDiagram] = useState<Diagram | undefined>();
  const [{ shape: selectedShape }, setSelectedShapeState] = useState<{
    shape: Shape<StateNode> | Connector | undefined;
  }>({ shape: undefined });
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [error, setError] = useState<FetchError | undefined>();
  const [debounceRefresh] = useTimeout();
  const {
    // workflowList,
    loadingError: findListError,
    findWorkflows
  } = useFindWorkflows();
  const {
    workflow: loadedWorkflowDto,
    loadingError: findError
  } = useFindWorkflow(workflowId, true);

  const {
    updatedWorkflow,
    updateError,
    updateWorkflowConfig
  } = useUpdateWorkflowConfig(workflowId);
  const apiError: FetchError | undefined =
    findError || updateError || findListError;

  const updateShape = () => setSelectedShapeState({ shape: selectedShape });
  const updateWorkflow = (shouldRefresh: boolean = true) => {
    if (workflow) {
      const workflowJson = workflow.toJson();
      console.log('workflowJson', workflowJson);
      updateWorkflowConfig(workflowJson);
      if (shouldRefresh) {
        setWorkflowState({ workflow });
      }
    }
  };

  const onShapeChange = () => {
    updateShape();
    updateWorkflow();
  };

  // get a list of all workflows for use in the workflow dropdown selector
  useEffect(() => {
    findWorkflows({ limit: 100 });
  }, [findWorkflows]);

  // get the config for the current workflow being edited
  useEffect(() => {
    if (loadedWorkflowDto) {
      // handle empty object
      const config = loadedWorkflowDto.config?.states
        ? loadedWorkflowDto.config
        : { name: loadedWorkflowDto.name, states: [{ name: 'Untitled' }] };
      setWorkflowState({ workflow: new Workflow(config) });
    }
  }, [loadedWorkflowDto]);

  useEffect(() => {
    // update editor with imported config
    if (config) {
      shouldRefreshRef.current = true;
      setWorkflowState({ workflow: new Workflow(config) });
      onChangeConfig?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  useEffect(() => {
    if (updatedWorkflow) {
      const config = workflow?.toJson();
      if (config) {
        onChange?.({ config });
      }
      setIsSaved(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedWorkflow]);

  useEffect(() => {
    apiError && setError(apiError);
  }, [apiError]);

  return (
    <Styled.SplitPane primary='next' className='workflow-edit-page'>
      <Styled.EditorPane flex className='editor-container'>
        {workflow && (
          <WorkflowDiagram
            workflow={workflow}
            type={DiagramType.State}
            onShapeChange={onShapeChange}
            onDiagramLoad={diagram => {
              setDiagram(diagram);
              if (!shouldRefreshRef.current) {
                return;
              }
              // refresh connectors
              shouldRefreshRef.current = false;
              const { shapes } = diagram.shape;
              diagram.shape.shapes = [];
              diagram.refresh();
              debounceRefresh(() => {
                diagram.shape.shapes = shapes;
                diagram.refresh();
              });
            }}
            onSelectionChange={shape => {
              setSelectedShapeState({
                shape: shape.type === ShapeType.State ? shape : undefined
              });
            }}
            onDelete={() => {
              console.log(
                'workflow.stateNode.states',
                workflow.stateNode.states
              );
              if (workflow.stateNode.states.length === 0) {
                workflow.stateNode.addState();
              }
              setSelectedShapeState({ shape: undefined });
              updateWorkflow();
            }}
          />
        )}
      </Styled.EditorPane>
      <SplitPane width={300}>
        {workflow &&
          selectedShape &&
          selectedShape instanceof Shape &&
          selectedShape.data && (
            <Styled.Configurator>
              <ConfigCard>
                <ConfigHeader>Info</ConfigHeader>
                <ConfigContent>
                  <ConfigFieldControl>
                    <TextField
                      className='field'
                      variant='outlined'
                      label='Name'
                      fullWidth
                      value={selectedShape.name}
                      onChange={event => {
                        selectedShape.name = event.target.value;
                        // selectedShape.data?.rename(event.target.value);
                        console.log('event.target.value', event.target.value);
                        workflow.renameStateNode(
                          selectedShape.data as StateNode,
                          event.target.value
                        );
                        console.log('workflow change', workflow);
                        updateShape();
                      }}
                      onBlur={() => {
                        updateWorkflow();
                      }}
                    />
                  </ConfigFieldControl>
                  <ConfigFieldControl>
                    <StateNodeTypeSelector
                      type={selectedShape.data?.type}
                      onChange={(type: StateNodeType) => {
                        if (selectedShape.data instanceof StateNode) {
                          selectedShape.data.type = type;
                          selectedShape.data.refreshType();
                          updateShape();
                          updateWorkflow();
                        }
                      }}
                    />
                  </ConfigFieldControl>
                </ConfigContent>
              </ConfigCard>
              <GroupPermissionsSection
                shape={selectedShape as Shape}
                onChange={(groups: Group[]) => {
                  if (!(selectedShape.data instanceof StateNode)) {
                    return;
                  }
                  if (!selectedShape.data.meta) {
                    selectedShape.data.meta = {};
                  }
                  if (
                    !Array.isArray(selectedShape.data.meta.groupPermissions)
                  ) {
                    selectedShape.data.meta.groupPermissions = [];
                  }
                  // save group ids
                  const groupIds: number[] = groups.map(
                    (group: Group) => group.id
                  );
                  const { groupPermissions } = selectedShape.data.meta;
                  groupPermissions.splice(0, groupPermissions.length);
                  groupPermissions.push(...groupIds);
                  updateShape();
                  updateWorkflow();
                }}
              />
              <EditableFieldSection
                shape={selectedShape as Shape}
                workflowDto={loadedWorkflowDto!}
                onChange={(fields: string[]) => {
                  if (!(selectedShape.data instanceof StateNode)) {
                    return;
                  }
                  // set meta and allowedFields if not existing
                  if (!selectedShape.data.meta) {
                    selectedShape.data.meta = {};
                  }
                  if (!Array.isArray(selectedShape.data.meta.allowedFields)) {
                    selectedShape.data.meta.allowedFields = [];
                  }
                  const { allowedFields } = selectedShape.data.meta;
                  allowedFields.splice(0, allowedFields.length);
                  allowedFields.push(...fields);
                  updateShape();
                  updateWorkflow();
                }}
              />
              <ConfigCard>
                <ConfigHeader>Color</ConfigHeader>
                <ConfigContent>
                  <ColorSelector
                    color={selectedShape?.data?.meta?.color}
                    onChange={(color: StatusColor | undefined) => {
                      if (selectedShape?.data instanceof StateNode) {
                        if (typeof selectedShape.data.meta === 'undefined') {
                          selectedShape.data.meta = {};
                        }
                        selectedShape.data.meta.color = color;
                        updateShape();
                        updateWorkflow();
                      }
                    }}
                  />
                </ConfigContent>
              </ConfigCard>
              <StateEventEditorContainer
                shape={selectedShape}
                editorProps={{
                  workflow,
                  updateShape,
                  updateWorkflow,
                  type: loadedWorkflowDto?.taskType
                    ?.targetEntity as TaskTypeEntity
                }}
                onChange={() => updateWorkflow()}
              />

              <StateInvokeActionEditor
                targetEntity={loadedWorkflowDto?.taskType?.targetEntity}
                shape={selectedShape}
                onChange={invoke => {
                  updateShape();
                  updateWorkflow();
                }}
              />
            </Styled.Configurator>
          )}

        <AppSnackbar
          open={!!error}
          severity='error'
          onClose={() => setError(undefined)}
        >
          {error?.message}
        </AppSnackbar>
        <AppSnackbar
          open={isSaved}
          severity='success'
          onClose={() => setIsSaved(false)}
        >
          Workflow Saved
        </AppSnackbar>
      </SplitPane>
    </Styled.SplitPane>
  );
};

export default WorkflowEditPage;
